import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, Ticket, X, Wrench, Package, ShoppingCart } from 'lucide-react';
import api from '../../lib/api';

const EVENT_CONFIG = {
  new_ticket: {
    icon: Ticket,
    color: 'from-red-600 to-rose-700',
    borderColor: 'border-red-500',
    glowColor: 'shadow-red-500/40',
    label: 'TICHET NOU',
    urgent_color: 'from-red-700 to-red-900',
    duration: 12000,
  },
  ticket_update: {
    icon: Wrench,
    color: 'from-blue-600 to-indigo-700',
    borderColor: 'border-blue-500',
    glowColor: 'shadow-blue-500/30',
    label: 'ACTUALIZARE TICHET',
    duration: 8000,
  },
};

const POLL_SECONDS = 5;

const AlertSplashScreen = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const shownIdsRef = useRef(new Set());
  const knownTicketIdsRef = useRef(null); // null = not initialized yet

  const addAlert = useCallback((type, data) => {
    const config = EVENT_CONFIG[type];
    if (!config) return;

    // Dedup by dedupId
    const key = data.dedupId || `${type}-${data.message}`;
    if (shownIdsRef.current.has(key)) return;
    shownIdsRef.current.add(key);
    setTimeout(() => shownIdsRef.current.delete(key), 30000);

    const alert = {
      id: `splash-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      type, config,
      title: data.title || config.label,
      message: data.message || '',
      subtext: data.subtext || '',
      priority: data.priority || 'normal',
      isUrgent: ['critical', 'urgent'].includes(data.priority),
      timestamp: new Date(),
    };

    setAlerts(prev => [alert, ...prev].slice(0, 3));

    const dur = alert.isUrgent ? config.duration * 1.5 : config.duration;
    setTimeout(() => setAlerts(prev => prev.filter(a => a.id !== alert.id)), dur);
  }, []);

  const dismissAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  // ===== Method 1: WebSocket / RealtimeContext events (instant when WS works) =====
  useRealtimeEvent('new_ticket', (msg) => {
    const d = msg.data || {};
    const isUrgent = ['critical', 'urgent'].includes(d.priority);
    addAlert('new_ticket', {
      title: `${isUrgent ? 'URGENT - ' : ''}TICHET NOU`,
      message: d.title || 'Tichet nou creat',
      subtext: [
        d.equipment_name && `Echipament: ${d.equipment_name}`,
        d.location_name && `Locatie: ${d.location_name}`,
        d.created_by_name && `Creat de: ${d.created_by_name}`,
        d.priority && `Prioritate: ${d.priority}`,
      ].filter(Boolean).join('  |  '),
      priority: d.priority,
      dedupId: d.id || d.number,
    });
  });

  useRealtimeEvent('ticket_update', (msg) => {
    const d = msg.data || {};
    if (msg.action === 'assigned' || msg.action === 'accepted') {
      addAlert('ticket_update', {
        title: msg.action === 'assigned' ? 'TICHET ASIGNAT' : 'TICHET ACCEPTAT',
        message: d.title || '',
        subtext: [
          d.equipment_name && `Echipament: ${d.equipment_name}`,
          d.assigned_to_name && `Asignat: ${d.assigned_to_name}`,
          d.status && `Status: ${d.status}`,
        ].filter(Boolean).join('  |  '),
        dedupId: `${d.id}-${msg.action}`,
      });
    }
  });

  // ===== Method 2: Direct ticket polling (works WITHOUT WebSocket/events) =====
  useEffect(() => {
    if (!user) return;

    const checkForNewTickets = async () => {
      try {
        const res = await api.get('/tickets?limit=10');
        const tickets = res.data || [];
        if (!tickets.length) return;

        const currentIds = new Set(tickets.map(t => t.id));

        // First run: just store IDs, don't trigger splash
        if (knownTicketIdsRef.current === null) {
          knownTicketIdsRef.current = currentIds;
          return;
        }

        // Find NEW tickets (IDs we haven't seen before)
        for (const t of tickets) {
          if (!knownTicketIdsRef.current.has(t.id)) {
            const isUrgent = ['critical', 'urgent'].includes(t.priority_requested);
            addAlert('new_ticket', {
              title: `${isUrgent ? 'URGENT - ' : ''}TICHET NOU #${t.number || ''}`,
              message: t.title || 'Tichet nou',
              subtext: [
                t.equipment_name && `Echipament: ${t.equipment_name}`,
                t.location_name && `Locatie: ${t.location_name}`,
                t.created_by_name && `Creat de: ${t.created_by_name}`,
                t.priority_requested && `Prioritate: ${t.priority_requested}`,
              ].filter(Boolean).join('  |  '),
              priority: t.priority_requested,
              dedupId: t.id,
            });
          }
        }

        // Update known IDs
        knownTicketIdsRef.current = currentIds;
      } catch {}
    };

    // First check after 2 seconds (let dashboard load first)
    const initTimer = setTimeout(checkForNewTickets, 2000);
    // Then check every POLL_SECONDS
    const interval = setInterval(checkForNewTickets, POLL_SECONDS * 1000);

    return () => {
      clearTimeout(initTimer);
      clearInterval(interval);
    };
  }, [user, addAlert]);

  if (alerts.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none flex flex-col items-center justify-start pt-4 gap-3" data-testid="alert-splash-container">
      {alerts.map((alert, idx) => {
        const Icon = alert.config.icon;
        const bgGradient = alert.isUrgent ? (alert.config.urgent_color || alert.config.color) : alert.config.color;

        return (
          <div
            key={alert.id}
            className={`pointer-events-auto w-[95vw] max-w-2xl rounded-2xl border-2 ${alert.config.borderColor} bg-gradient-to-r ${bgGradient} text-white shadow-2xl ${alert.config.glowColor} overflow-hidden`}
            style={{
              animation: 'splashSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              opacity: idx === 0 ? 1 : 0.9 - idx * 0.15,
              transform: `scale(${1 - idx * 0.03})`,
            }}
            data-testid={`alert-splash-${alert.id}`}
          >
            <div className="h-1 bg-white/20">
              <div
                className="h-full bg-white/60 rounded-r"
                style={{ animation: `splashProgress ${alert.isUrgent ? alert.config.duration * 1.5 : alert.config.duration}ms linear forwards` }}
              />
            </div>

            <div className="px-5 py-4 flex items-start gap-4">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 ${alert.isUrgent ? 'animate-pulse' : ''}`}>
                {alert.isUrgent ? <AlertTriangle className="w-7 h-7 text-yellow-300" /> : <Icon className="w-6 h-6" />}
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] font-black tracking-[0.15em] uppercase bg-white/20 px-2 py-0.5 rounded">{alert.title}</span>
                  {alert.isUrgent && (
                    <span className="text-[10px] font-black tracking-wider uppercase bg-yellow-400 text-red-900 px-2 py-0.5 rounded animate-pulse">URGENT</span>
                  )}
                </div>
                <p className="text-lg sm:text-xl font-bold leading-tight mt-1">{alert.message}</p>
                {alert.subtext && <p className="text-xs sm:text-sm text-white/80 mt-2 leading-relaxed">{alert.subtext}</p>}
              </div>
              <button onClick={() => dismissAlert(alert.id)} className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors active:scale-90" data-testid={`dismiss-splash-${alert.id}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes splashSlideIn {
          0% { transform: translateY(-100px) scale(0.8); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes splashProgress {
          0% { width: 100%; }
          100% { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default AlertSplashScreen;
